import React from "react";
import { Trash2 } from "lucide-react";
import { ResponsiveDataList } from "../../../../../components/responsive";
import Helpers from "../../../../../utils/helpers";
import type { LifecycleRetreat } from "../../../../../types/retreatLifecycle";
import {
  formatLanguage,
  formatRetreatDateRange,
  statusChip,
} from "../../lifecycle/lifecycleUi";
import { SectionShell } from "../../lifecycle/SectionShell";
import {
  countLinkCell,
  retreatTitleCell,
  tooltipCell,
} from "../../lifecycle/manageRetreatColumns";
import { DeletedRetreatCard } from "../cards/DeletedRetreatCard";
import { SectionActionButtons } from "../SectionActionButtons";
import { SECTION_IDS } from "../manageRetreatTypes";

export function DeletedSection({
  rows,
  total,
  loading,
  searchValue,
  onSearchChange,
  dataFetchApi,
  isSuperAdmin,
  onHistory,
  onDelete,
  onApprove,
  onRestore,
}: {
  rows: LifecycleRetreat[];
  total: number;
  loading: boolean;
  searchValue: string;
  onSearchChange: (v: string) => void;
  dataFetchApi: (...args: any[]) => void;
  isSuperAdmin: boolean;
  onHistory: (r: LifecycleRetreat) => void;
  onDelete: (r: LifecycleRetreat) => void;
  onApprove: (r: LifecycleRetreat) => void;
  onRestore: (r: LifecycleRetreat) => void;
}) {
  const columns = [
    {
      id: "action",
      name: "Action",
      sortable: false,
      button: true,
      grow: 0,
      cell: (r: LifecycleRetreat) => (
        <SectionActionButtons
          r={r}
          kind="deleted"
          isSuperAdmin={isSuperAdmin}
          onHistory={onHistory}
          onDelete={onDelete}
          onApprove={onApprove}
          onRestore={onRestore}
        />
      ),
      width: "170px",
    },
    {
      id: "retreatId",
      name: "Retreat Id",
      sortable: true,
      grow: 0,
      selector: (r: LifecycleRetreat) => r.id,
      width: "120px",
    },
    {
      id: "title",
      name: "Retreat",
      sortable: true,
      grow: 0,
      cell: retreatTitleCell,
      width: "300px",
    },
    {
      id: "language",
      name: "Language",
      sortable: true,
      grow: 0,
      selector: (r: LifecycleRetreat) => formatLanguage(r.language),
      cell: (r: LifecycleRetreat) => tooltipCell(formatLanguage(r.language)),
      width: "130px",
    },
    {
      id: "location",
      name: "Location",
      sortable: true,
      grow: 0,
      selector: (r: LifecycleRetreat) => r.location || "",
      cell: (r: LifecycleRetreat) =>
        tooltipCell(
          r.location
            ? `${r.location.charAt(0).toUpperCase()}${r.location.slice(1)}`
            : "—",
        ),
      width: "130px",
    },
    {
      id: "dateFrom",
      name: "Dates",
      sortable: true,
      grow: 0,
      selector: (r: LifecycleRetreat) =>
        formatRetreatDateRange(r.dateFrom, r.dateTo),
      cell: (r: LifecycleRetreat) =>
        tooltipCell(formatRetreatDateRange(r.dateFrom, r.dateTo)),
      width: "150px",
    },
    {
      id: "Retreatants",
      name: "# Retreatants",
      selector: (r: LifecycleRetreat) => r.countRetreatant,
      cell: (r: LifecycleRetreat) =>
        countLinkCell(
          r.countRetreatant,
          `/retreatRoster?retreatId=${r.id}&flag=1`,
        ),
      reorder: true,
      sortable: true,
      grow: 0,
      width: "150px",
      style: { cursor: "text" },
    },
    {
      id: "team",
      name: "# Teams",
      selector: (r: LifecycleRetreat) => r.countTeam,
      cell: (r: LifecycleRetreat) =>
        countLinkCell(r.countTeam, `/retreatRoster?retreatId=${r.id}&flag=2`),
      reorder: true,
      sortable: true,
      grow: 0,
      width: "120px",
      style: { cursor: "text" },
    },
    {
      id: "created",
      name: "Created",
      sortable: true,
      grow: 0,
      selector: (r: LifecycleRetreat) => r.createdAt || "",
      cell: (r: LifecycleRetreat) =>
        tooltipCell(
          Helpers.convertUtcToTimezone(
            r.createdAt || "",
            "MM-DD-YYYY hh:mm:a",
          ),
        ),
      width: "170px",
    },
    {
      id: "lifecycleStatus",
      name: "Status",
      sortable: true,
      grow: 0,
      cell: () => tooltipCell(statusChip("Deleted"), "Deleted"),
      width: "130px",
    },
  ];

  return (
    <SectionShell
      id={SECTION_IDS.deleted}
      title="Deleted / Backup Retreats"
      subtitle="Soft-deleted retreats. Restore to return them to the prior lifecycle stage."
      bgcolor="#f3f4f6"
      icon={<Trash2 size={22} color="#6b7280" />}
      total={total}
      searchValue={searchValue}
      onSearchChange={onSearchChange}
    >
      <ResponsiveDataList
        data={rows}
        total={total}
        loading={loading}
        isServerSide
        sortServer
        countPerPage={10}
        dataFetchApi={dataFetchApi}
        columns={columns}
        cardGridColumns={{ xs: "1fr", md: "repeat(2, minmax(0, 1fr))" }}
        renderCard={(r) => (
          <DeletedRetreatCard
            r={r}
            isSuperAdmin={isSuperAdmin}
            onHistory={onHistory}
            onDelete={onDelete}
            onApprove={onApprove}
            onRestore={onRestore}
          />
        )}
      />
    </SectionShell>
  );
}
